"use client";

import { useState } from "react";
import Link from "next/link";
import { Check, Zap, Building2, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

type BillingInterval = "monthly" | "annual";

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  name: string;
  icon: typeof Zap;
  description: string;
  priceMonthly: number;
  priceAnnual: number;
  popular?: boolean;
  badge?: string;
  features: PlanFeature[];
  cta: string;
  ctaHref: string;
}

const PLANS: Plan[] = [
  {
    name: "Free",
    icon: Shield,
    description: "Try it out with 2 free contract scans",
    priceMonthly: 0,
    priceAnnual: 0,
    features: [
      { text: "2 contract scans", included: true },
      { text: "1 property check (upload only)", included: true },
      { text: "Full risk analysis", included: true },
      { text: "12 Indian languages", included: true },
      { text: "PDF export", included: false },
      { text: "Auto-fetch government docs", included: false },
      { text: "Priority processing", included: false },
    ],
    cta: "Get Started Free",
    ctaHref: "/auth/signup",
  },
  {
    name: "Basic",
    icon: Zap,
    description: "For individuals reviewing multiple contracts",
    priceMonthly: 299,
    priceAnnual: 2990,
    features: [
      { text: "10 contract scans/month", included: true },
      { text: "1 property check/month (upload)", included: true },
      { text: "Full risk analysis", included: true },
      { text: "12 Indian languages", included: true },
      { text: "PDF export", included: true },
      { text: "Auto-fetch government docs", included: false },
      { text: "Priority processing", included: false },
    ],
    cta: "Subscribe — Basic",
    ctaHref: "/auth/signup?plan=basic",
  },
  {
    name: "Pro",
    icon: Zap,
    description: "For professionals and small businesses",
    priceMonthly: 699,
    priceAnnual: 6990,
    popular: true,
    features: [
      { text: "15 contract scans/month", included: true },
      { text: "1 property case/month (upload)", included: true },
      { text: "Full risk analysis", included: true },
      { text: "12 Indian languages", included: true },
      { text: "PDF export", included: true },
      { text: "Auto-fetch government docs", included: false },
      { text: "Priority processing", included: true },
    ],
    cta: "Subscribe — Pro",
    ctaHref: "/auth/signup?plan=pro",
  },
  {
    name: "Property Pro",
    icon: Building2,
    description: "Auto-fetch government docs + deep analysis",
    priceMonthly: 1999,
    priceAnnual: 19990,
    badge: "AUTO-FETCH",
    features: [
      { text: "15 contract scans/month", included: true },
      { text: "3 property cases/month", included: true },
      { text: "Auto-fetch from govt portals", included: true },
      { text: "MH, KA, TS, TN states supported", included: true },
      { text: "PDF export", included: true },
      { text: "12 Indian languages", included: true },
      { text: "Priority processing", included: true },
    ],
    cta: "Subscribe — Property Pro",
    ctaHref: "/auth/signup?plan=property_pro",
  },
  {
    name: "Enterprise",
    icon: Building2,
    description: "For law firms, banks, and real estate agencies",
    priceMonthly: 4999,
    priceAnnual: 49990,
    badge: "BEST VALUE",
    features: [
      { text: "Unlimited contract scans", included: true },
      { text: "10 auto-fetch property cases/month", included: true },
      { text: "Auto-fetch from govt portals", included: true },
      { text: "All supported states", included: true },
      { text: "PDF export", included: true },
      { text: "Encumbrance monitoring (soon)", included: true },
      { text: "Priority processing", included: true },
    ],
    cta: "Subscribe — Enterprise",
    ctaHref: "/auth/signup?plan=enterprise",
  },
];

export default function PricingPage() {
  const [interval, setInterval] = useState<BillingInterval>("monthly");

  return (
    <main className="mx-auto max-w-6xl px-4 py-16">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-foreground">Simple, transparent pricing</h1>
        <p className="mt-3 text-muted-foreground max-w-lg mx-auto">
          Start with 2 free scans. Pay per scan or save with a subscription.
          All prices include 18% GST.
        </p>

        {/* Billing toggle */}
        <div className="mt-6 inline-flex items-center gap-2 rounded-lg border border-border bg-card p-1">
          <button
            onClick={() => setInterval("monthly")}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
              interval === "monthly"
                ? "bg-accent text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground/80"
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval("annual")}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
              interval === "annual"
                ? "bg-accent text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground/80"
            )}
          >
            Annual
            <span className="ml-1.5 rounded-full bg-green-900/40 px-2 py-0.5 text-[10px] font-semibold text-green-400">
              2 months free
            </span>
          </button>
        </div>
      </div>

      {/* Per-scan pricing callout */}
      <div className="mb-10 rounded-lg border border-border bg-secondary p-4 text-center">
        <p className="text-sm text-foreground/80">
          Don&apos;t need a subscription?{" "}
          <span className="font-semibold text-foreground">Pay per scan: ₹99/contract</span>
          {" "}&middot;{" "}
          <span className="font-semibold text-foreground">₹499/property (upload)</span>
          {" "}&middot;{" "}
          <span className="font-semibold text-amber-400">₹999/property (auto-fetch)</span>
        </p>
      </div>

      {/* Plan cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {PLANS.map((plan) => {
          const price = interval === "monthly" ? plan.priceMonthly : plan.priceAnnual;
          const monthlyEquiv = interval === "annual" && price > 0
            ? Math.round(price / 12)
            : null;

          return (
            <div
              key={plan.name}
              className={cn(
                "relative flex flex-col rounded-xl border p-6",
                plan.popular
                  ? "border-blue-700/50 bg-blue-950/10 shadow-lg shadow-blue-900/10"
                  : plan.badge
                    ? "border-amber-700/50 bg-amber-950/10"
                    : "border-border bg-secondary"
              )}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-0.5 text-[10px] font-bold text-foreground uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              {!plan.popular && plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-600 px-3 py-0.5 text-[10px] font-bold text-foreground uppercase tracking-wider whitespace-nowrap">
                  {plan.badge}
                </div>
              )}

              <div className="mb-4">
                <plan.icon
                  size={20}
                  className={plan.popular ? "text-blue-400" : "text-muted-foreground"}
                />
                <h2 className="mt-2 text-lg font-bold text-foreground">{plan.name}</h2>
                <p className="text-xs text-muted-foreground">{plan.description}</p>
              </div>

              <div className="mb-6">
                {price === 0 ? (
                  <div className="text-3xl font-bold text-foreground">Free</div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-foreground">₹{price.toLocaleString("en-IN")}</span>
                      <span className="text-sm text-muted-foreground">/{interval === "monthly" ? "mo" : "yr"}</span>
                    </div>
                    {monthlyEquiv && (
                      <p className="text-xs text-green-400 mt-1">
                        ₹{monthlyEquiv.toLocaleString("en-IN")}/mo equivalent
                      </p>
                    )}
                  </>
                )}
              </div>

              <ul className="mb-6 flex-1 space-y-2">
                {plan.features.map((f) => (
                  <li key={f.text} className="flex items-start gap-2 text-xs">
                    <Check
                      size={14}
                      className={cn(
                        "mt-0.5 shrink-0",
                        f.included ? "text-green-500" : "text-muted-foreground/50"
                      )}
                    />
                    <span className={f.included ? "text-foreground/80" : "text-muted-foreground/60 line-through"}>
                      {f.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                href={plan.ctaHref}
                className={cn(
                  buttonVariants({
                    variant: plan.popular ? "default" : "outline",
                    size: "sm",
                  }),
                  "w-full justify-center text-xs"
                )}
              >
                {plan.cta}
              </Link>
            </div>
          );
        })}
      </div>

      {/* GST notice */}
      <p className="mt-8 text-center text-xs text-muted-foreground/60">
        All prices are in Indian Rupees (₹) and include 18% GST (SAC 998314).
        GST invoices are generated automatically for all purchases.
      </p>
    </main>
  );
}
